import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import { useGetAlertsQuery, useSendAlertMutation } from '../../app/services/alertService';
import TableUI from '../../components/tables/TableUi';
import { useState } from 'react';
import { PlusCircle } from 'lucide-react';
import AlertForm from './components/AlertForm';
import { Chip } from '@mui/material';
import { Button } from '../../components/ui/button';
import AlertFilterSidebar from '../../components/filters/AlertFilterSidebar';
import { useForm } from 'react-hook-form';
import { Form, FormField, FormItem, FormLabel, FormControl } from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import PageSizeSelector from '../../components/filters/PageSizeSelector';
import InstitutionSelector from '../../components/filters/InstitutionSelector';
import { useGetInstitutionListsQuery } from '../../app/services/selectListService';
import { PermissionGuard } from '../../components/common/PermissionGuard';
import { usePermissions } from '../../hooks/usePermissions';
import { toastResponseHandler } from '../../lib/toastMessageHandler';
import DeleteAlerts from './components/DeleteAlerts';
import { useDialog } from '../../contexts/dialog-context';
import { getAlertColumns } from './components/Columns';

const Alerts = () => {
  var { openDialog } = useDialog();
  const { canEdit, canDelete } = usePermissions();
  // Filter states
  const [institutionId, setInstitutionId] = useState<number | undefined>();
  const [titleFilter, setTitleFilter] = useState<string>("");
  const [alertTypeFilter, setAlertTypeFilter] = useState<Alert.AlertType | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const form = useForm<Alert.AlertFilter>({
    defaultValues: {
      institutionId: undefined,
      title: "",
      alertType: undefined,
      pageSize: 10
    }
  });

  const { data: institutionsData } = useGetInstitutionListsQuery();


  const { data, isLoading, isFetching } = useGetAlertsQuery({
    Page: currentPage,
    Offset: pageSize,
    InstitutionId: institutionId,
    Title: titleFilter,
    AlertType: alertTypeFilter,
  });
  const [sendAlert] = useSendAlertMutation();
  const alerts = Array.isArray(data?.data?.data) ? data.data.data : [];
  const totalPages = pageSize === 0 ? 1 : Math.ceil((data?.data?.total || 0) / pageSize);

  const applyFilters = (data: Alert.AlertFilter) => {
    if (data.institutionId !== undefined) {
      setInstitutionId(data.institutionId);
    }
    if (data.title !== undefined) {
      setTitleFilter(data.title);
    }
    if (data.alertType !== undefined) {
      setAlertTypeFilter(data.alertType);
    }
    if (data.pageSize !== undefined) {
      setPageSize(data.pageSize);
    }
    form.setValue("searchText", data.searchText || "");
    form.setValue("institutionId", data.institutionId);
    form.setValue("title", data.title || "");
    form.setValue("alertType", data.alertType);
    form.setValue("pageSize", data.pageSize || 10);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    form.reset({
      searchText: "",
      institutionId: undefined,
      title: "",
      alertType: undefined,
      pageSize: 10
    });
    setInstitutionId(undefined);
    setTitleFilter("");
    setAlertTypeFilter(undefined);
    setPageSize(10);
    setCurrentPage(1);
  };

  // Filter functions
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Event handlers
  const handleEdit = (alert: Alert.AlertDto) => {
    openDialog({
      title: 'Edit Alert',
      dividers: true,
      content: <AlertForm
        mode='edit'
        alert={alert}
      />
    });
  };

  const handleDelete = (alert: Alert.AlertDto) => {
    openDialog({
      title: 'Delete Alert',
      maxWidth: 'xs',
      content: <DeleteAlerts title={alert.title} id={alert.id} />
    });
  };

  const handleSendAlert = async (alert: Alert.AlertDto) => {
    try {
      var response = await sendAlert(alert.id).unwrap();
      toastResponseHandler({ response });
    } catch (error: any) {
      toastResponseHandler({ response: error?.data as any });
    }
  };

  const handleCreateAlert = () => {
    openDialog({
      title: 'Add New Alert',
      dividers: true,
      content: <AlertForm
        mode='create'
      />
    });
  };

  const columns = getAlertColumns({
    canEdit: canEdit('alerts'),
    onDelete: handleDelete,
    onSendAlert: handleSendAlert,
    onEdit: handleEdit,
    canDelete: canDelete('alerts')
  });

  return (
    <>
      <PageBreadcrumb pageTitle="Alerts" />
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {(institutionId || titleFilter || alertTypeFilter || pageSize !== 10) && (
              <div className='flex gap-2 flex-wrap'>
                {institutionId && (
                  <Chip
                    label={`Institution: ${institutionsData?.data.find(inst => inst.id === institutionId.toString())?.name || institutionId}`}
                    variant="outlined"
                    size="small"
                    onDelete={() => {
                      setInstitutionId(undefined);
                      form.setValue("institutionId", undefined);
                    }}
                  />
                )}
                {titleFilter && (
                  <Chip
                    label={`Title: ${titleFilter}`}
                    variant="outlined"
                    size="small"
                    onDelete={() => {
                      setTitleFilter("");
                      form.setValue("title", "");
                    }}
                  />
                )}
                {alertTypeFilter && (
                  <Chip
                    label={`Alert Type: ${alertTypeFilter}`}
                    variant="outlined"
                    size="small"
                    onDelete={() => {
                      setAlertTypeFilter(undefined);
                      form.setValue("alertType", undefined);
                    }}
                  />
                )}
                {pageSize !== 10 && (
                  <Chip
                    label={`Page Size: ${pageSize === 0 ? 'All' : pageSize}`}
                    variant="outlined"
                    size="small"
                    onDelete={() => {
                      setPageSize(10);
                      form.setValue("pageSize", 10);
                      setCurrentPage(1);
                    }}
                  />
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <AlertFilterSidebar
              reset={resetFilters}
              applyFilters={() => {
                const formData = form.getValues();
                applyFilters(formData);
              }}
              isLoading={isLoading || isFetching}
            >
              <Form {...form}>
                <div className="px-6 py-4 space-y-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">Title Filter</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Filter by title..."
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <InstitutionSelector
                    control={form.control}
                    name="institutionId"
                    label="Institution"
                    placeholder="Select institution"
                  />
                  <FormField
                    control={form.control}
                    name="alertType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">Alert Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger className='w-full'>
                              <SelectValue placeholder="Select alert type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Info">Info</SelectItem>
                            <SelectItem value="Warning">Warning</SelectItem>
                            <SelectItem value="Emergency">Emergency</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <PageSizeSelector
                    control={form.control}
                    name="pageSize"
                  />
                </div>
              </Form>
            </AlertFilterSidebar>
            <PermissionGuard action='create' module='alerts'>
              <Button
                onClick={handleCreateAlert}
                className="flex items-center gap-2"
              >
                <PlusCircle size={16} />
                Add Alerts
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Table */}
        <TableUI
          data={alerts || []}
          columns={columns}
          isFetching={isLoading || isFetching}
          EmptyText="No alerts found"
          pageCount={pageSize === 0 ? undefined : totalPages}
          page={pageSize === 0 ? undefined : handlePageChange}
          showSerialNumber={true}
          currentPage={currentPage}
          pageSize={pageSize}
          key={`pagination-${currentPage}-${pageSize}`}
        />
      </div>
    </>
  );
};

export default Alerts;
